import { login, logout, getUserInfo, getSelectableMonth, getSelectableMonthToNow, refreshToken } from '@/api/user';
import { setLoginUser, removeLoginUser, getLoginDate, getExpireDate, getToken, getLoginUser } from '@/utils/auth';
import { employeeFind } from '@/api/authority/employee'
import { resetRouter } from '@/router';
import { getCurrentAccountSet, removeCurrentAccountSet } from '@/api/accountSet';

const normalizeCode = code => {
  if (code === undefined || code === null || code === '') {
    return null;
  }
  return String(code);
};

const toAuthorityMap = authorityList => {
  const list = Array.isArray(authorityList) ? authorityList : [];
  return list.reduce((result, item) => {
    if (item && typeof item === 'object') {
      if (item.enable === false || item.enable === 0 || item.enable === '0') {
        return result;
      }
      const normalizedCode = normalizeCode(item.code);
      if (normalizedCode) {
        result[normalizedCode] = true;
      }
      return result;
    }
    const normalizedCode = normalizeCode(item);
    if (normalizedCode) {
      result[normalizedCode] = true;
    }
    return result;
  }, {});
};

const normalizeAuthorityPayload = payload => {
  if (!payload) {
    return {
      permissionCodes: [],
      menuCodes: []
    };
  }

  if (Array.isArray(payload)) {
    return {
      permissionCodes: payload,
      menuCodes: []
    };
  }

  if (payload.list && !payload.permissionCodes && !payload.menuCodes) {
    return normalizeAuthorityPayload(payload.list);
  }

  return {
    permissionCodes: Array.isArray(payload.permissionCodes) ? payload.permissionCodes : [],
    menuCodes: Array.isArray(payload.menuCodes) ? payload.menuCodes : []
  };
};

const hasOwn = (target, key) => Object.prototype.hasOwnProperty.call(target || {}, key);

const pickUserInfoValue = (payload, groupName, fieldName) => {
  const group = payload && payload[groupName];
  if (group && hasOwn(group, fieldName)) {
    return group[fieldName];
  }
  return undefined;
};

const normalizeUserInfoPayload = payload => {
  const data = payload || {};
  const normalized = {
    ...data,
    areaId: pickUserInfoValue(data, 'user', 'areaId'),
    userId: pickUserInfoValue(data, 'user', 'userId'),
    account: pickUserInfoValue(data, 'user', 'account'),
    storeName: pickUserInfoValue(data, 'user', 'storeName'),
    jobNumber: pickUserInfoValue(data, 'user', 'jobNumber'),
    jobName: pickUserInfoValue(data, 'user', 'jobName'),
    storeType: pickUserInfoValue(data, 'user', 'storeType'),
    startDateTime: pickUserInfoValue(data, 'user', 'startDateTime'),
    endDatetime: pickUserInfoValue(data, 'user', 'endDatetime'),
    accountSetCode: pickUserInfoValue(data, 'accountSet', 'code'),
    type: pickUserInfoValue(data, 'accountSet', 'type'),
    accountSetType: pickUserInfoValue(data, 'accountSet', 'accountSetType'),
    enableAssist: pickUserInfoValue(data, 'accountSet', 'enableAssist'),
    softVersion: pickUserInfoValue(data, 'accountSet', 'softVersion'),
    expireWarnDays: pickUserInfoValue(data, 'expireReminder', 'warnDays'),
    expireStoreCount: pickUserInfoValue(data, 'expireReminder', 'storeCount'),
    expireStoreList: pickUserInfoValue(data, 'expireReminder', 'storeList')
  };

  if (normalized.jobName) {
    normalized.cashier_name = normalized.jobName;
  }

  return normalized;
};

const applyAuthData = (commit, data) => {
  commit('SET_TOKEN', data.token);
  commit('SET_LOGIN_DATE', new Date(data.loginDate));
  commit('SET_EXPIRE_DATE', data.vipStatus !== 1 ? data.expireDate : null);
  commit('SET_IS_CASHIER', !!data.jobNumber);
  setLoginUser(data);
};

const clearSessionState = commit => {
  commit('SET_TOKEN', '');
  commit('SET_ROLES', []);
  commit('SET_USER', null);
  commit('SET_AUTHORITY', null);
  commit('SET_IS_CASHIER', false);
  removeLoginUser();
  removeCurrentAccountSet();
  resetRouter();
};

const initialLoginUser = getLoginUser();

const state = {
  token: getToken(),
  expireDate: getExpireDate(),
  name: '',
  avatar: '',
  introduction: '',
  roles: [],
  user: null,
  authority: {},
  menus: {},
  loginDate: getLoginDate(),
  assistVisible: null,
  isCashier: !!(initialLoginUser && initialLoginUser.jobNumber), // 是否是收银员
  // 可选日期 账簿与核对总账模块需要
  selectableMonth: getSelectableMonth,
  selectableMonthToNow: getSelectableMonthToNow
};

const mutations = {
  SET_ASSIST_VISIBLE: (state, assistVisible) => {
    state.assistVisible = assistVisible;
  },
  SET_TOKEN: (state, token) => {
    state.token = token;
  },
  SET_INTRODUCTION: (state, introduction) => {
    state.introduction = introduction;
  },
  SET_EXPIRE_DATE: (state, expireDate) => {
    state.expireDate = expireDate;
  },
  SET_NAME: (state, name) => {
    state.name = name;
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar;
  },
  SET_ROLES: (state, roles) => {
    state.roles = roles;
  },
  SET_USER: (state, user) => {
    state.user = user;
  },
  SET_ACCOUNT_SET_TYPE: (state, accountSetType) => {
    if (!state.user) {
      return;
    }
    state.user = {
      ...state.user,
      accountSetType,
      accountSet: {
        ...(state.user.accountSet || {}),
        accountSetType
      }
    };
  },
  SET_AUTHORITY: (state, data) => {
    const normalizedData = normalizeAuthorityPayload(data);
    state.authority = toAuthorityMap(normalizedData.permissionCodes);
    state.menus = toAuthorityMap(normalizedData.menuCodes);
  },
  SET_LOGIN_DATE: (state, loginDate) => {
    state.loginDate = loginDate;
  },
  SET_IS_CASHIER: (state, isCashier) => {
    state.isCashier = isCashier;
  }
};

const actions = {
  // user login
  async login ({ commit }, userInfo) {
    const {
      account,
      password,
      employeeAccount,
      loginDate,
      rememberMe,
      rootCode,
      captchaId,
      captchaCode
    } = userInfo;
    const response = await login({
      account: account.trim(),
      password: password,
      employeeAccount,
      loginDate,
      rememberMe,
      rootCode,
      captchaId,
      captchaCode
    });
    const { data } = response;
    applyAuthData(commit, data);
    return data;
  },

  async refreshToken ({ commit }) {
    const response = await refreshToken();
    const { data } = response;
    applyAuthData(commit, data);
    return data;
  },

  // get user info
  async getInfo ({ commit, state, dispatch }) {
    const { data } = await getUserInfo();
    if (!data) {
      throw new Error('Verification failed, please Login again.');
    }
    const normalizedData = normalizeUserInfoPayload(data);
    const { accountSetCode } = normalizedData;

    const loginUser = getLoginUser() || {};
    setLoginUser({
      ...loginUser,
      ...normalizedData,
      token: state.token || loginUser.token
    });

    commit('SET_USER', normalizedData);
    commit('SET_ASSIST_VISIBLE', !!normalizedData.enableAssist);
    commit('SET_IS_CASHIER', !!normalizedData.jobNumber);

    dispatch('app/setCurrentAccountSet', {
      code: accountSetCode || '',
      version: Date.now()
    }, { root: true });

    try {
      const response = await employeeFind();
      commit('SET_AUTHORITY', response && response.data);
    } catch (error) {
      console.error('[user/getInfo] employeeFind failed, fallback to empty authority', error);
      if (state.isCashier) {
        commit('SET_AUTHORITY', null);
      }
    }
    return normalizedData;
  },

  // user logout
  async logout ({ commit, state, dispatch }) {
    await logout(state.token);
    clearSessionState(commit);

    // reset visited views and cached views
    // to fixed https://github.com/PanJiaChen/vue-element-admin/issues/2485
    dispatch('tagsView/delAllViews', null, { root: true });
    dispatch('permission/resetRouters', null, { root: true });

    if (window._reportSDK && window._reportSDK.setUserId) {
      window._reportSDK.setUserId(-1);
    }
  },

  // remove token
  resetToken ({ commit }) {
    clearSessionState(commit);
  },

  async checkLogin ({ state, dispatch }) {
    if (state.user !== null) {
      return true;
    }
    try {
      await dispatch('getInfo');
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  },
  checkAccountSet () {
    return !!getCurrentAccountSet();
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
